//@flow
import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {usePage} from '../../../page/utils';
import {Grid, Card, Hidden} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import Skeleton from '@material-ui/lab/Skeleton';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {useHistory, Switch, Route} from 'react-router-dom';
import {colors} from '../../../../assets/styles/colors';
import TaxCard from './TaxCard/TaxCard';
import TaxContainer from '../containers/TaxContainer';
import TierRestrictionModal from '../../../../elements/TierRestrictionModal/TierRestrictionModal';
import {useIsFeatureInTier} from '../../../../utils/component';

import type {
  Currency,
  Tax as TaxType,
  CouponState,
  Store,
} from '../../../../entities';
import UpgradeIcon from '../../../../elements/icons/UpgradeIcon';

type Props = {|
  isFetching?: boolean,
  saveSuccess?: boolean,
  taxes?: TaxType[],
  error?: string,
  currency?: Currency,
  toggleTaxesFilter: ({taxFilterState?: string}) => void,
  resetState: () => void,
  fetchTaxes: () => void,
  taxFilterState: CouponState,
  store: Store,
|};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    padding: '16px',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      background: 'transparent',
    },
  },
  filterWrapper: {
    marginBottom: '16px',
    [theme.breakpoints.down('sm')]: {
      background: '#FFFFFF',
      padding: '16px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      borderRadius: '12px',
    },
  },
  bodyContent: {
    [theme.breakpoints.down('sm')]: {
      background: '#FFFFFF',
      padding: '16px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      borderRadius: '12px',
    },
  },
  filterButton: {
    color: colors.grey600,
    fontWeight: 'normal',
    borderRadius: '8px',
    padding: '4px 16px',
    marginRight: theme.spacing(1),
  },
  filterButtonActive: {
    color: colors.purple3,
    backgroundColor: colors.lightPurple,
    fontWeight: 600,
  },
  skeleton: {
    borderRadius: '8px',
  },
  whiteButton: {
    backgroundColor: 'white',

    color: colors.purple3,
    fontWeight: 600,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    '&:hover': {
      backgroundColor: colors.grey200,
    },
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
    },
    marginLeft: 4,
    marginBottom: 12,
  },
  whiteButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'center',
  },
}));

export default usePage<Props>(Taxes, 'Taxes');

function Taxes(props: Props) {
  const {
    resetState,
    isFetching,
    fetchTaxes,
    saveSuccess,
    taxes,
    error,
    currency,
    toggleTaxesFilter,
    taxFilterState,
    store,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const [openModalProTier, setOpenModalProTier] = useState(false);
  const hasTaxesFeature = useIsFeatureInTier('TAXES');

  useEffect(() => {
    if (hasTaxesFeature) fetchTaxes();
  }, [fetchTaxes, hasTaxesFeature, taxFilterState]);

  const handleEnabledOption = (state?: CouponState) => {
    toggleTaxesFilter({taxFilterState: state});
  };

  const handleCreateTax = () => {
    resetState();
    if (hasTaxesFeature) history.push(`/configure/taxes/new`);
  };

  const allowedForCreateTax = useMemo(() => {
    return true;
    // return taxes ? taxes.filter(el => el.enabled).length === 0 : false;
  }, []);

  const filterWrapper = (
    <>
      {hasTaxesFeature && (
        <div className={classes.filterWrapper}>
          <CustomButton
            className={`${classes.filterButton} ${
              taxFilterState === '0' ? classes.filterButtonActive : ''
            }`}
            onClick={() => handleEnabledOption('0')}
          >
            Activos
          </CustomButton>
          <CustomButton
            className={`${classes.filterButton} ${
              taxFilterState === '1' ? classes.filterButtonActive : ''
            }`}
            onClick={() => handleEnabledOption('1')}
          >
            Inactivos
          </CustomButton>
          <CustomButton
            className={`${classes.filterButton} ${
              taxFilterState === '' ? classes.filterButtonActive : ''
            }`}
            onClick={() => handleEnabledOption()}
          >
            Ver Todos
          </CustomButton>
        </div>
      )}
    </>
  );

  return (
    <Switch>
      <Route exact path="/configure/taxes">
        <TierRestrictionModal
          open={openModalProTier}
          handleClose={() => setOpenModalProTier(false)}
        />
        {hasTaxesFeature && <SnackBar mode="error" message={error} />}
        {saveSuccess && (
          <SnackBar
            mode="success"
            className={classes.snackBarError}
            message="Guardado correctamente"
          />
        )}
        <Hidden mdUp>{filterWrapper}</Hidden>
        <Card className={classes.root}>
          <div className={classes.bodyContent}>
            <Grid container justify="space-between" direccion="row">
              <Hidden smDown>{filterWrapper}</Hidden>
              {!hasTaxesFeature && (
                <div>
                  <CustomButton
                    className={classes.whiteButton}
                    to="/tiers-list"
                  >
                    <div className={classes.whiteButtonLabel}>
                      <span>
                        Mejora tu plan para acceder a esta funcionalidad
                      </span>
                      <UpgradeIcon width={18} height={18} />
                    </div>
                  </CustomButton>
                </div>
              )}
            </Grid>
            <Grid
              container
              direction="row"
              justify="flex-start"
              alignItems="stretch"
              spacing={3}
            >
              <Grid item xs={12} md={6} lg={4} xl={3}>
                <TaxCard
                  type="CREATE"
                  handleClick={handleCreateTax}
                  disabledCreate={!allowedForCreateTax}
                />
              </Grid>
              {isFetching && !taxes && !currency
                ? Array.from({length: 2}, (x, i) => (
                    <Grid item xs={12} md={6} lg={4} xl={3} key={i}>
                      <Skeleton
                        className={classes.skeleton}
                        variant="rect"
                        height={216}
                      />
                    </Grid>
                  ))
                : taxes && taxes.length > 0
                ? taxes.map((tax: TaxType, index: number) => (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      lg={4}
                      xl={3}
                      key={`tax-${index}`}
                    >
                      <TaxCard
                        tax={tax}
                        currency={currency}
                        store={store}
                        resetState={resetState}
                      />
                    </Grid>
                  ))
                : null}
            </Grid>
          </div>
        </Card>
      </Route>
      <Route exact path="/configure/taxes/:taxId" component={TaxContainer} />
    </Switch>
  );
}

Taxes.propTypes = {
  taxes: PropTypes.array,
  fetchTaxes: PropTypes.func.isRequired,
  saveSuccess: PropTypes.bool,
  isFetching: PropTypes.bool,
  error: PropTypes.string,
  currency: PropTypes.object,
  taxFilterState: PropTypes.string,
  resetState: PropTypes.func.isRequired,
  toggleTaxesFilter: PropTypes.func.isRequired,
  store: PropTypes.object.isRequired,
  information: PropTypes.object,
};
